import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, DatePicker, Select, Button, Modal, Form, Input, Switch,
  Popconfirm, message, Spin, ConfigProvider, theme, Space, InputNumber
} from 'antd';
import {
  BarChartOutlined, UserOutlined, RobotOutlined, NotificationOutlined,
  LogoutOutlined, SearchOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, ReloadOutlined, DownloadOutlined,
  PictureOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import './AdminDashboard.css';
/**
 * 从管理端 Presenter 导入真实接口
 * getModels: 获取渠道可用模型列表（接口文档 #14）
 */
import { getModels } from './AdminDashboardPresenter';
import { getLocalStorage } from '../../utils/localStorageUtil';
import { logoutPortalSession } from '../../utils/portalSession';
import { LOCAL_STORAGE_KEYS } from '../../const/localStorageConst';
import { getAgentIconSelectOptions } from '../../const/agentIcons';
import fgLogo from '../../imgs/fg_logo.png';
import {
  useModelTokenStats,
  useUserTokenStats,
  useChannelTokenStats,
  useUserManagement,
  useAgentManagement,
  useAnnouncementManagement,
  getEffectiveUsername
} from '../../utils/adminDashboardUtils';

const { RangePicker } = DatePicker;

// ===================== CSV 导出工具 =====================
const exportToCSV = (data, columns, filename) => {
  if (!data || data.length === 0) {
    message.warning('没有可导出的数据');
    return;
  }
  const headers = columns.map(col => col.title).join(',');
  const rows = data.map(item =>
    columns.map(col => {
      const value = item[col.key];
      const str = String(value ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(',')
  );
  const csvContent = [headers, ...rows].join('\n');
  const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadTextFile = (content, filename, type = 'image/svg+xml;charset=utf-8') => {
  const blob = new Blob([content], { type });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const escapeSvgText = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const formatChartValue = (value, unit) => {
  const num = Number(value) || 0;
  return unit === 'wan' ? `${(num / 10000).toFixed(2).replace(/\.00$/, '')}万` : `${num}`;
};

const getChartValue = (record, fieldKey, unit) => {
  const raw = Number(record?.[fieldKey]) || 0;
  return unit === 'wan' ? raw / 10000 : raw;
};

const generateBarChartSvg = ({ data, xKey, fieldKey, fieldLabel, unit, title }) => {
  const safeData = Array.isArray(data) ? data : [];
  const barCount = safeData.length;
  const left = 82;
  const right = 40;
  const top = 44;
  const bottom = 120;
  const barWidth = 44;
  const gap = 28;
  const chartWidth = Math.max(780, left + right + barCount * barWidth + Math.max(barCount - 1, 0) * gap);
  const chartHeight = 520;
  const plotWidth = chartWidth - left - right;
  const plotHeight = chartHeight - top - bottom;
  const values = safeData.map(item => getChartValue(item, fieldKey, unit));
  const maxValue = Math.max(...values, 1);
  const unitText = unit === 'wan' ? '万Token' : 'Token';
  const ticks = Array.from({ length: 5 }, (_, index) => maxValue * index / 4);

  const bars = safeData.map((item, index) => {
    const value = values[index];
    const rawValue = Number(item?.[fieldKey]) || 0;
    const barHeight = maxValue ? (value / maxValue) * plotHeight : 0;
    const x = left + index * (barWidth + gap) + Math.max((plotWidth - (barCount * barWidth + Math.max(barCount - 1, 0) * gap)) / 2, 0);
    const y = top + plotHeight - barHeight;
    const name = escapeSvgText(item?.[xKey] || '-');
    const label = escapeSvgText(formatChartValue(rawValue, unit));

    return `
      <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" fill="#7ec8ff" />
      <text x="${x + barWidth / 2}" y="${Math.max(y - 10, top - 8)}" text-anchor="middle" fill="#1f2937" font-size="12" font-weight="600" font-family="Arial, Microsoft YaHei, sans-serif">${label}</text>
      <text x="${x + barWidth / 2}" y="${top + plotHeight + 32}" text-anchor="end" transform="rotate(-35 ${x + barWidth / 2} ${top + plotHeight + 32})" fill="#334155" font-size="12" font-family="Arial, Microsoft YaHei, sans-serif">${name}</text>
    `;
  }).join('');

  const gridLines = ticks.map((tick) => {
    const y = top + plotHeight - (tick / maxValue) * plotHeight;
    return `
      <line x1="${left}" y1="${y}" x2="${chartWidth - right}" y2="${y}" stroke="#d9e2ef" stroke-width="1" />
      <text x="${left - 12}" y="${y + 4}" text-anchor="end" fill="#475569" font-size="12" font-family="Arial, Microsoft YaHei, sans-serif">${escapeSvgText(unit === 'wan' ? tick.toFixed(2).replace(/\.00$/, '') : Math.round(tick))}</text>
    `;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${chartWidth}" height="${chartHeight}" viewBox="0 0 ${chartWidth} ${chartHeight}">
  <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" fill="#ffffff" />
  <text x="${left - 52}" y="${top - 18}" fill="#475569" font-size="12" font-family="Arial, Microsoft YaHei, sans-serif">${unitText}</text>
  ${gridLines}
  <line x1="${left}" y1="${top}" x2="${left}" y2="${top + plotHeight}" stroke="#94a3b8" stroke-width="1.2" />
  <line x1="${left}" y1="${top + plotHeight}" x2="${chartWidth - right}" y2="${top + plotHeight}" stroke="#94a3b8" stroke-width="1.2" />
  ${bars}
</svg>`;
};

const tokenChartFields = [
  { label: '输入Token', value: 'prompt_tokens' },
  { label: '输出Token', value: 'completion_tokens' },
  { label: '总Token', value: 'total_tokens' },
];

const userTokenChartFields = [
  { label: '总输入Token', value: 'total_prompt_tokens' },
  { label: '总输出Token', value: 'total_completion_tokens' },
  { label: '总Token', value: 'total_tokens' },
];

// ===================== 列定义 =====================

// --- Token统计 - 模型维度 ---
const modelTokenColumns = [
  { title: '用户名', dataIndex: 'username', key: 'username', sorter: (a, b) => a.username.localeCompare(b.username) },
  { title: '模型名称', dataIndex: 'model_name', key: 'model_name', sorter: (a, b) => a.model_name.localeCompare(b.model_name) },
  { title: '输入Token', dataIndex: 'prompt_tokens', key: 'prompt_tokens', sorter: (a, b) => a.prompt_tokens - b.prompt_tokens },
  { title: '输出Token', dataIndex: 'completion_tokens', key: 'completion_tokens', sorter: (a, b) => a.completion_tokens - b.completion_tokens },
  { title: '总Token', dataIndex: 'total_tokens', key: 'total_tokens', sorter: (a, b) => a.total_tokens - b.total_tokens, defaultSortOrder: 'descend' },
];

const modelTokenExportCols = [
  { key: 'username', title: '用户名' },
  { key: 'model_name', title: '模型名称' },
  { key: 'prompt_tokens', title: '输入Token' },
  { key: 'completion_tokens', title: '输出Token' },
  { key: 'total_tokens', title: '总Token' },
];

// --- Token统计 - 用户维度(父表) ---
const userTokenColumns = [
  { title: '用户名', dataIndex: 'username', key: 'username', sorter: (a, b) => a.username.localeCompare(b.username) },
  { title: '总输入Token', dataIndex: 'total_prompt_tokens', key: 'total_prompt_tokens', sorter: (a, b) => a.total_prompt_tokens - b.total_prompt_tokens },
  { title: '总输出Token', dataIndex: 'total_completion_tokens', key: 'total_completion_tokens', sorter: (a, b) => a.total_completion_tokens - b.total_completion_tokens },
  { title: '总Token', dataIndex: 'total_tokens', key: 'total_tokens', sorter: (a, b) => a.total_tokens - b.total_tokens, defaultSortOrder: 'descend' },
];

const userTokenExportCols = [
  { key: 'username', title: '用户名' },
  { key: 'total_prompt_tokens', title: '总输入Token' },
  { key: 'total_completion_tokens', title: '总输出Token' },
  { key: 'total_tokens', title: '总Token' },
];

// --- Token统计 - 用户维度(嵌套子表) ---
const userTokenDetailColumns = [
  { title: '模型名称', dataIndex: 'model_name', key: 'model_name' },
  { title: '输入Token', dataIndex: 'prompt_tokens', key: 'prompt_tokens', sorter: (a, b) => a.prompt_tokens - b.prompt_tokens },
  { title: '输出Token', dataIndex: 'completion_tokens', key: 'completion_tokens', sorter: (a, b) => a.completion_tokens - b.completion_tokens },
  { title: '总Token', dataIndex: 'total_tokens', key: 'total_tokens', sorter: (a, b) => a.total_tokens - b.total_tokens },
];

// --- Token统计 - 渠道模型 ---
const channelTokenColumns = [
  { title: '模型名称', dataIndex: 'model_name', key: 'model_name', sorter: (a, b) => a.model_name.localeCompare(b.model_name) },
  { title: '输入Token', dataIndex: 'prompt_tokens', key: 'prompt_tokens', sorter: (a, b) => a.prompt_tokens - b.prompt_tokens },
  { title: '输出Token', dataIndex: 'completion_tokens', key: 'completion_tokens', sorter: (a, b) => a.completion_tokens - b.completion_tokens },
  { title: '总Token', dataIndex: 'total_tokens', key: 'total_tokens', sorter: (a, b) => a.total_tokens - b.total_tokens, defaultSortOrder: 'descend' },
];

const channelTokenExportCols = [
  { key: 'model_name', title: '模型名称' },
  { key: 'prompt_tokens', title: '输入Token' },
  { key: 'completion_tokens', title: '输出Token' },
  { key: 'total_tokens', title: '总Token' },
];

// --- 用户管理 ---
// 列筛选：文本包含匹配（输入框 + 筛选/重置按钮）
const buildTextFilterDropdown = (placeholder) => ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
  <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
    <Input
      placeholder={placeholder}
      value={selectedKeys[0]}
      onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      onPressEnter={() => confirm()}
      style={{ marginBottom: 8, display: 'block', width: 200 }}
    />
    <Space>
      <Button type="primary" size="small" onClick={() => confirm()}>筛选</Button>
      <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>重置</Button>
    </Space>
  </div>
);

// 列筛选：创建时间范围
const buildDateRangeFilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
  <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
    <RangePicker
      value={selectedKeys.length === 2 ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])] : []}
      onChange={(dates) => setSelectedKeys(
        dates && dates[0] && dates[1]
          ? [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]
          : []
      )}
      allowClear
      style={{ marginBottom: 8 }}
    />
    <Space>
      <Button type="primary" size="small" onClick={() => confirm()}>筛选</Button>
      <Button size="small" onClick={() => { clearFilters?.(); confirm(); }}>重置</Button>
    </Space>
  </div>
);

const getUserColumns = (handleDelete, handleEditPwd, handleEditAlias) => [
  {
    title: '用户名', dataIndex: 'username', key: 'username',
    sorter: (a, b) => (a.username || '').localeCompare(b.username || ''),
    filterDropdown: buildTextFilterDropdown('搜索用户名'),
    onFilter: (value, record) => String(record.username || '').toLowerCase().includes(String(value).toLowerCase()),
  },
  {
    title: '短用户名', dataIndex: 'username_alias', key: 'username_alias',
    sorter: (a, b) => getEffectiveUsername(a).localeCompare(getEffectiveUsername(b)),
    filterDropdown: buildTextFilterDropdown('搜索短用户名'),
    onFilter: (value, record) => getEffectiveUsername(record).toLowerCase().includes(String(value).toLowerCase()),
    render: (val, record) => {
      const effective = getEffectiveUsername(record);
      return val
        ? <span>{effective}</span>
        : <span className="user-alias-derived" title="未设置独立短用户名，默认取邮箱 @ 前部分">{effective}</span>;
    }
  },
  {
    title: '邮箱', dataIndex: 'email', key: 'email',
    sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    filterDropdown: buildTextFilterDropdown('搜索邮箱'),
    onFilter: (value, record) => String(record.email || '').toLowerCase().includes(String(value).toLowerCase()),
  },
  {
    title: '密码', dataIndex: 'pwd', key: 'pwd',
    sorter: (a, b) => (a.pwd || '').localeCompare(b.pwd || ''),
    filterDropdown: buildTextFilterDropdown('搜索密码'),
    onFilter: (value, record) => String(record.pwd || '').toLowerCase().includes(String(value).toLowerCase()),
  },
  {
    title: '强制修改密码', dataIndex: 'force_update_password', key: 'force_update_password',
    sorter: (a, b) => String(a.force_update_password ?? '').localeCompare(String(b.force_update_password ?? '')),
    filters: [
      { text: '强制修改', value: '0' },
      { text: '不强制', value: '1' },
    ],
    onFilter: (value, record) => String(record.force_update_password ?? '') === value,
    render: (val) => String(val) === '0'
      ? <span className="status-tag status-tag--maintenance">强制修改</span>
      : <span className="status-tag status-tag--active">不强制</span>
  },
  {
    title: '是否管理员', dataIndex: 'is_admin', key: 'is_admin',
    sorter: (a, b) => (a.is_admin ? 1 : 0) - (b.is_admin ? 1 : 0),
    filters: [
      { text: '管理员', value: '1' },
      { text: '普通用户', value: '0' },
    ],
    onFilter: (value, record) => (value === '1') === Boolean(record.is_admin),
    render: (val) => val ? <span className="status-tag status-tag--admin">管理员</span> : <span className="status-tag">普通用户</span>
  },
  {
    title: '在线', dataIndex: 'online', key: 'online',
    sorter: (a, b) => (a.online ? 1 : 0) - (b.online ? 1 : 0),
    filters: [
      { text: '是', value: '1' },
      { text: '否', value: '0' },
    ],
    onFilter: (value, record) => (value === '1') === Boolean(record.online),
    render: (val) => val
      ? <span className="status-tag status-tag--active">是</span>
      : <span className="status-tag status-tag--disabled">否</span>
  },
  {
    title: '创建时间', dataIndex: 'created_at', key: 'created_at',
    sorter: (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
    filterDropdown: buildDateRangeFilterDropdown,
    onFilter: (value, record) => {
      if (!Array.isArray(value) || value.length !== 2) return true;
      const day = dayjs(record.created_at).format('YYYY-MM-DD');
      return day >= value[0] && day <= value[1];
    },
    render: (t) => dayjs(t).format('YYYY-MM-DD')
  },
  {
    title: '操作', key: 'action', width: 230,
    render: (_, record) => (
      <Space>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditPwd(record)}>修改密码</Button>
        <Button type="link" size="small" icon={<UserOutlined />} onClick={() => handleEditAlias(record)}>短用户名</Button>
        <Popconfirm
          title="确认删除"
          description={`确定要删除用户 ${record.username} 吗？`}
          onConfirm={() => handleDelete(record.user_id)}
          okText="确认"
          cancelText="取消"
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )
  }
];

const userExportCols = [
  { key: 'user_id', title: '用户ID' },
  { key: 'username', title: '用户名' },
  { key: 'email', title: '邮箱' },
  { key: 'pwd', title: '密码' },
  { key: 'is_admin', title: '是否管理员' },
  { key: 'created_at', title: '创建时间' },
];

// --- AI工具管理 ---
const getAgentColumns = (handleEdit, handleDelete) => [
  { title: '工具名称', dataIndex: 'agent_name', key: 'agent_name', sorter: (a, b) => a.agent_name.localeCompare(b.agent_name) },
  { title: '标签', dataIndex: 'agent_tags', key: 'agent_tags' },
  { title: '类型', dataIndex: 'agent_type', key: 'agent_type', sorter: (a, b) => (a.agent_type || '').localeCompare(b.agent_type || '') },
  { title: '描述', dataIndex: 'agent_des', key: 'agent_des', ellipsis: true },
  { title: '跳转链接', dataIndex: 'agent_jump_url', key: 'agent_jump_url', ellipsis: true,
    render: (url) => url ? <a href={url} target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>链接</a> : '-'
  },
  { title: '支持邮箱', dataIndex: 'is_support_email', key: 'is_support_email', width: 100,
    render: (value) => Number(value ?? 1) === 1 ? '支持' : '不支持'
  },
  { title: '状态', dataIndex: 'status', key: 'status', sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
    render: (status) => {
      const map = { '1': { cls: 'active', text: '启用中' }, '2': { cls: 'maintenance', text: '维护中' }, '3': { cls: 'disabled', text: '停用' } };
      const info = map[status] || { cls: 'disabled', text: '未知' };
      return <span className={`status-tag status-tag--${info.cls}`}>{info.text}</span>;
    }
  },
  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', sorter: (a, b) => a.created_at.localeCompare(b.created_at),
    render: (t) => dayjs(t).format('YYYY-MM-DD HH:mm')
  },
  {
    title: '操作', key: 'action', width: 160,
    render: (_, record) => (
      <Space>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
        <Popconfirm
          title="确认删除"
          description={`确定要删除工具 ${record.agent_name} 吗？`}
          onConfirm={() => handleDelete(record.agent_id)}
          okText="确认"
          cancelText="取消"
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )
  }
];

const agentExportCols = [
  { key: 'agent_name', title: '工具名称' },
  { key: 'agent_tags', title: '标签' },
  { key: 'agent_type', title: '类型' },
  { key: 'agent_des', title: '描述' },
  { key: 'agent_jump_url', title: '跳转链接' },
  { key: 'is_support_email', title: '支持邮箱' },
  { key: 'status', title: '状态' },
  { key: 'created_at', title: '创建时间' },
];

// --- 公告管理 ---
const getAnnouncementColumns = (handleEdit, handleDelete) => [
  { title: 'ID', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id },
  { title: '公告内容', dataIndex: 'public_content', key: 'public_content', ellipsis: true },
  { title: '发布时间', dataIndex: 'created_time', key: 'created_time', sorter: (a, b) => a.created_time.localeCompare(b.created_time), defaultSortOrder: 'descend',
    render: (t) => dayjs(t).format('YYYY-MM-DD HH:mm')
  },
  {
    title: '操作', key: 'action', width: 160,
    render: (_, record) => (
      <Space size={0}>
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
        <Popconfirm
          title="确认删除"
          description="确定要删除这条公告吗？"
          onConfirm={() => handleDelete(record.id)}
          okText="确认"
          cancelText="取消"
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
        </Popconfirm>
      </Space>
    )
  }
];

const announcementExportCols = [
  { key: 'id', title: 'ID' },
  { key: 'public_content', title: '公告内容' },
  { key: 'created_time', title: '发布时间' },
];

// ===================== 状态文本映射 =====================
const statusOptions = [
  { label: '启用中', value: '1' },
  { label: '维护中', value: '2' },
  { label: '停用', value: '3' },
];

const agentTypeOptions = [
  { label: '对话', value: 'chat' },
  { label: '代码', value: 'code' },
  { label: '数据', value: 'data' },
  { label: '翻译', value: 'translate' },
  { label: '网盘', value: 'pan' },
];

// ===================== 主组件 =====================
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // --- 全局状态 ---
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({});
  const [activeSection, setActiveSection] = useState('token-stats');
  const [models, setModels] = useState([]);

  // --- 使用自定义 Hooks ---
  const modelToken = useModelTokenStats(messageApi);
  const userToken = useUserTokenStats(messageApi);
  const channelToken = useChannelTokenStats(messageApi);
  const userMgmt = useUserManagement(messageApi);
  const agentMgmt = useAgentManagement(messageApi);
  const announcementMgmt = useAnnouncementManagement(messageApi);

  // --- Form instances (UI层管理) ---
  const [addUserForm] = Form.useForm();
  const [editPwdForm] = Form.useForm();
  const [aliasForm] = Form.useForm();
  const [agentForm] = Form.useForm();
  const [addAnnouncementForm] = Form.useForm();
  const [editAnnouncementForm] = Form.useForm();
  const [userChartForm] = Form.useForm();
  const [channelChartForm] = Form.useForm();
  // 修改密码弹窗的新密码输入框引用：弹窗打开后主动聚焦，避免 antd 抢焦点导致用户以为无法输入
  const editPwdInputRef = useRef(null);
  const [userChartModalVisible, setUserChartModalVisible] = useState(false);
  const [channelChartModalVisible, setChannelChartModalVisible] = useState(false);

  // 修改密码弹窗打开后自动聚焦新密码输入框（motion 关闭时 afterOpenChange 不会触发，故用 effect）
  useEffect(() => {
    if (userMgmt.editPwdModalVisible) {
      const timer = setTimeout(() => editPwdInputRef.current?.focus(), 120);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [userMgmt.editPwdModalVisible]);

  // 短用户名弹窗打开时回填当前值
  useEffect(() => {
    if (userMgmt.aliasModalVisible) {
      aliasForm.setFieldsValue({
        username_alias: userMgmt.editingAliasUser?.username_alias || '',
      });
    }
  }, [userMgmt.aliasModalVisible, userMgmt.editingAliasUser, aliasForm]);

  // ===================== 初始化 =====================
  useEffect(() => {
    const stored = getLocalStorage(LOCAL_STORAGE_KEYS.USER_INFO);
    if (stored) {
      try {
        setUserInfo(typeof stored === 'string' ? JSON.parse(stored) : stored);
      } catch { setUserInfo({}); }
    }
    initLoad();
  }, []);

  const initLoad = async () => {
    setLoading(true);
    try {
      const [modelsRes] = await Promise.all([getModels()]);
      if (modelsRes.status === 200) {
        setModels(modelsRes.data || []);
      }
      // 三张Token统计表初始化加载
      await Promise.all([
        modelToken.handleSearch(),
        userToken.handleSearch(),
        channelToken.handleSearch(),
      ]);
    } catch (err) {
      console.error('初始化加载失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // ===================== 锚点导航滚动监听 =====================
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    const sections = ['token-stats', 'user-management', 'agent-management', 'announcement-management'];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [loading]);

  // ===================== 退出登录 =====================
  const handleLogout = async () => {
    await logoutPortalSession();
    navigate('/login');
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  // ===================== AI工具 Modal 操作 =====================
  const handleOpenAgentModal = (mode, record = null) => {
    agentMgmt.setModalMode(mode);
    if (mode === 'edit' && record) {
      agentForm.setFieldsValue({
        agent_id: record.agent_id,
        agent_name: record.agent_name,
        agent_tags: record.agent_tags,
        agent_type: record.agent_type,
        agent_icon: record.agent_icon || '',
        agent_des: record.agent_des,
        agent_jump_url: record.agent_jump_url,
        is_support_email: Number(record.is_support_email ?? 1) === 1,
        status: record.status,
      });
    }
    agentMgmt.setModalVisible(true);
  };

  const handleSubmitAgent = async () => {
    try {
      const values = await agentForm.validateFields();
      await agentMgmt.handleSubmitAgent(values, agentForm, agentMgmt.modalMode);
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('操作失败');
    }
  };

  const handleEditPwdSubmit = async () => {
    try {
      const values = await editPwdForm.validateFields();
      await userMgmt.handleUpdatePwd(values, editPwdForm);
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('更新密码失败');
    }
  };

  const handleAliasSubmit = async () => {
    try {
      const values = await aliasForm.validateFields();
      await userMgmt.handleSaveAlias(values, aliasForm);
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('更新短用户名失败');
    }
  };

  const handleAddUserSubmit = async () => {
    try {
      const values = await addUserForm.validateFields();
      await userMgmt.handleAddUser(values, addUserForm);
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('添加用户失败');
    }
  };

  const handleAddAnnouncementSubmit = async () => {
    try {
      const values = await addAnnouncementForm.validateFields();
      await announcementMgmt.handleAddAnnouncement(values, addAnnouncementForm);
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('发布失败');
    }
  };

  const handleOpenAnnouncementEdit = (record) => {
    editAnnouncementForm.setFieldsValue({
      id: record.id,
      public_content: record.public_content,
      created_time: record.created_time ? dayjs(record.created_time) : null,
    });
    announcementMgmt.handleOpenEditAnnouncement(record);
  };

  const handleEditAnnouncementSubmit = async () => {
    try {
      const values = await editAnnouncementForm.validateFields();
      await announcementMgmt.handleEditAnnouncement(values, editAnnouncementForm);
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('更新失败');
    }
  };

  const handleExportBarChart = async ({ type }) => {
    const isUserChart = type === 'user';
    const form = isUserChart ? userChartForm : channelChartForm;
    const sourceData = isUserChart ? userToken.filteredData : channelToken.filteredData;
    const xKey = isUserChart ? 'username' : 'model_name';
    const title = isUserChart ? '用户维度Token统计柱状图' : '渠道模型Token统计柱状图';
    const fieldOptions = isUserChart ? userTokenChartFields : tokenChartFields;

    try {
      const values = await form.validateFields();
      const fieldKey = values.fieldKey;
      const fieldLabel = fieldOptions.find(item => item.value === fieldKey)?.label || 'Token';
      const limit = Number(values.limit) || 10;
      const sortedData = [...(sourceData || [])]
        .sort((a, b) => {
          const diff = (Number(a?.[fieldKey]) || 0) - (Number(b?.[fieldKey]) || 0);
          return values.sortOrder === 'asc' ? diff : -diff;
        })
        .slice(0, limit);

      if (!sortedData.length) {
        messageApi.warning('没有可生成图片的数据');
        return;
      }

      const svg = generateBarChartSvg({
        data: sortedData,
        xKey,
        fieldKey,
        fieldLabel,
        unit: values.unit,
        title,
      });

      downloadTextFile(svg, `${title}_${dayjs().format('YYYYMMDDHHmmss')}.svg`);
      messageApi.success('柱状图已生成并下载');
      if (isUserChart) {
        setUserChartModalVisible(false);
      } else {
        setChannelChartModalVisible(false);
      }
      form.resetFields();
    } catch (err) {
      if (err.errorFields) return;
      messageApi.error('生成柱状图失败');
    }
  };

  // ===================== 导航项 =====================
  const navItems = [
    { key: 'token-stats', title: 'Token统计', icon: <BarChartOutlined /> },
    { key: 'user-management', title: '用户管理', icon: <UserOutlined /> },
    { key: 'agent-management', title: 'AI工具管理', icon: <RobotOutlined /> },
    { key: 'announcement-management', title: '公告管理', icon: <NotificationOutlined /> },
  ];

  // ===================== Render =====================
  if (loading) {
    return (
      <div className="admin-dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  const userColumns = getUserColumns(userMgmt.handleDeleteUser, userMgmt.handleOpenEditPwdModal, userMgmt.handleOpenAliasModal);
  const agentColumns = getAgentColumns((record) => handleOpenAgentModal('edit', record), agentMgmt.handleDeleteAgent);
  const announcementColumns = getAnnouncementColumns(handleOpenAnnouncementEdit, announcementMgmt.handleDeleteAnnouncement);

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          // 关闭动画：antd v6 关闭弹窗的动画可能卡死，残留全屏遮罩导致页面无法点击/输入
          motion: false,
          colorPrimary: '#667eea',
          borderRadius: 8,
          colorBgContainer: 'transparent',
          colorBgElevated: 'rgba(26,31,78,0.95)',
          colorBorder: 'rgba(255,255,255,0.1)',
          colorText: '#e0e7ff',
          colorTextSecondary: 'rgba(255,255,255,0.6)',
        },
      }}
    >
      <div className="admin-dashboard">
        {contextHolder}

        {/* 强制去掉所有表格分割线 */}
        <style>{`
          .admin-dashboard .ant-table,
          .admin-dashboard .ant-table-container,
          .admin-dashboard .ant-table-content,
          .admin-dashboard .ant-table-header,
          .admin-dashboard .ant-table-body {
            border: none !important;
            box-shadow: none !important;
          }
          .admin-dashboard .ant-table-thead > tr > th,
          .admin-dashboard .ant-table-tbody > tr > td,
          .admin-dashboard .ant-table-cell,
          .admin-dashboard .ant-table-cell::before,
          .admin-dashboard .ant-table-cell::after {
            border: none !important;
            box-shadow: none !important;
          }
          .admin-dashboard .ant-table-container::before,
          .admin-dashboard .ant-table-container::after,
          .admin-dashboard .ant-table-ping-left::before,
          .admin-dashboard .ant-table-ping-right::after,
          .admin-dashboard .ant-table-ping-left .ant-table-cell-fix-left-last::after,
          .admin-dashboard .ant-table-ping-right .ant-table-cell-fix-right-first::before {
            display: none !important;
          }
          .admin-dashboard .ant-table-column-sorter {
            color: #9fb4dd !important;
          }
          .admin-dashboard .ant-table-column-sorter-up.active,
          .admin-dashboard .ant-table-column-sorter-down.active {
            color: #7ea9ff !important;
          }
          .admin-dashboard .ant-table-column-sorters:hover .ant-table-column-sorter {
            color: #c9d8ff !important;
          }
        `}</style>

        {/* 背景装饰 */}
        <div className="admin-dashboard__glow admin-dashboard__glow--1" />
        <div className="admin-dashboard__glow admin-dashboard__glow--2" />
        <div className="admin-dashboard__glow admin-dashboard__glow--3" />

        {/* 顶部导航 */}
        <header className="admin-header">
          <div className="admin-header__logo">
            <img src={fgLogo} alt="logo" className="admin-header__logo-img" />
            <div>
              <div className="admin-header__brand">富小助平台</div>
              <div className="admin-header__brand-sub">管理控制台</div>
            </div>
          </div>
          <div className="admin-header__right">
            <div className="admin-header__user">
              <div className="admin-header__avatar">
                {userInfo.username?.charAt(0).toUpperCase() || 'A'}
              </div>
              <span className="admin-header__username">{userInfo.username || '管理员'}</span>
              <span className="admin-header__admin-badge">管理员</span>
            </div>
            <button className="admin-header__logout" onClick={handleLogout}>
              <LogoutOutlined style={{ marginRight: 4 }} />退出
            </button>
          </div>
        </header>

        {/* 主布局 */}
        <div className="admin-layout">
          {/* 侧边导航 */}
          <aside className="admin-sidebar">
            <nav className="anchor-nav">
              {navItems.map((item) => (
                <div
                  key={item.key}
                  className={`anchor-nav__item ${activeSection === item.key ? 'anchor-nav__item--active' : ''}`}
                  onClick={() => scrollToSection(item.key)}
                >
                  <span className="anchor-nav__icon">{item.icon}</span>
                  <span>{item.title}</span>
                </div>
              ))}
            </nav>
          </aside>

          {/* 内容区 */}
          <main className="admin-content">

            {/* ==================== 模块1: 大模型Token统计 ==================== */}
            <section id="token-stats" className="admin-section">
              <div className="module-card">
                <div className="module-card__header">
                  <div className="section-title">
                    <div className="section-title__icon section-title__icon--blue">
                      <BarChartOutlined />
                    </div>
                    <span className="section-title__text">大模型Token统计</span>
                    <div className="section-title__line" />
                  </div>
                </div>
                <div className="module-card__content">

                  {/* 表1: 模型维度Token统计 */}
                  <div className="token-table-section">
                    <div className="token-table-section__title-row">
                      <div className="token-table-section__title">模型和人维度Token统计</div>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => exportToCSV(modelToken.filteredData, modelTokenExportCols, `模型维度Token统计_${dayjs().format('YYYYMMDDHHmmss')}.csv`)}
                      >
                        导出CSV
                      </Button>
                    </div>
                    <div className="filter-bar">
                      <span className="filter-bar__label">时间范围</span>
                      <RangePicker
                        showTime
                        value={modelToken.dateRange}
                        onChange={(dates) => modelToken.setDateRange(dates)}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                        style={{ width: 420 }}
                      />
                      <span className="filter-bar__label">用户</span>
                      <Select
                        mode="multiple"
                        placeholder="全部用户"
                        allowClear
                        maxTagCount={2}
                        style={{ minWidth: 200 }}
                        value={modelToken.selectedUsers}
                        onChange={(values) => modelToken.setSelectedUsers(values || [])}
                        options={modelToken.userOptions.map(u => ({ label: u, value: u }))}
                      />
                      <span className="filter-bar__label">模型</span>
                      <Select
                        mode="multiple"
                        placeholder="全部模型"
                        allowClear
                        maxTagCount={2}
                        style={{ minWidth: 200 }}
                        value={modelToken.selectedModels}
                        onChange={(values) => modelToken.setSelectedModels(values || [])}
                        options={modelToken.modelOptions.map(m => ({ label: m, value: m }))}
                      />
                      <div style={{ flex: 1 }} />
                      <Button type="primary" icon={<SearchOutlined />} onClick={modelToken.handleSearch}>查询</Button>
                      <Button icon={<ReloadOutlined />} onClick={modelToken.handleRefresh}>刷新</Button>
                    </div>
                    <div className="table-fixed-height">
                      <Table
                        bordered={false}
                        showSorterTooltip={false}
                        columns={modelTokenColumns}
                        dataSource={modelToken.filteredData}
                        loading={modelToken.loading}
                        rowKey={(record) => `${record.user_id}_${record.model_name}`}
                        scroll={{ y: 300 }}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  </div>

                  {/* 表2: 用户维度Token统计（嵌套子表） */}
                  <div className="token-table-section">
                    <div className="token-table-section__title-row">
                      <div className="token-table-section__title">用户维度Token统计</div>
                      <Space>
                        <Button
                          size="small"
                          icon={<PictureOutlined />}
                          onClick={() => setUserChartModalVisible(true)}
                        >
                          生成柱状图
                        </Button>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => exportToCSV(userToken.filteredData, userTokenExportCols, `用户维度Token统计_${dayjs().format('YYYYMMDDHHmmss')}.csv`)}
                        >
                          导出CSV
                        </Button>
                      </Space>
                    </div>
                    <div className="filter-bar">
                      <span className="filter-bar__label">时间范围</span>
                      <RangePicker
                        showTime
                        value={userToken.dateRange}
                        onChange={(dates) => userToken.setDateRange(dates)}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                        style={{ width: 420 }}
                      />
                      <span className="filter-bar__label">用户</span>
                      <Select
                        mode="multiple"
                        placeholder="全部用户"
                        allowClear
                        maxTagCount={2}
                        style={{ minWidth: 200 }}
                        value={userToken.selectedUsers}
                        onChange={(values) => userToken.setSelectedUsers(values || [])}
                        options={userToken.userOptions.map(u => ({ label: u, value: u }))}
                      />
                      <div style={{ flex: 1 }} />
                      <Button type="primary" icon={<SearchOutlined />} onClick={userToken.handleSearch}>查询</Button>
                      <Button icon={<ReloadOutlined />} onClick={userToken.handleRefresh}>刷新</Button>
                    </div>
                    <div className="table-fixed-height">
                      <Table
                        bordered={false}
                        showSorterTooltip={false}
                        columns={userTokenColumns}
                        dataSource={userToken.filteredData}
                        loading={userToken.loading}
                        rowKey="username"
                        scroll={{ y: 300 }}
                        pagination={false}
                        size="small"
                        expandable={{
                          expandedRowRender: (record) => (
                            <Table
                              bordered={false}
                              showSorterTooltip={false}
                              columns={userTokenDetailColumns}
                              dataSource={record.details || []}
                              rowKey="model_name"
                              pagination={false}
                              size="small"
                            />
                          ),
                        }}
                      />
                    </div>
                  </div>

                  {/* 表3: 渠道模型Token统计 */}
                  <div className="token-table-section">
                    <div className="token-table-section__title-row">
                      <div className="token-table-section__title">渠道模型Token统计</div>
                      <Space>
                        <Button
                          size="small"
                          icon={<PictureOutlined />}
                          onClick={() => setChannelChartModalVisible(true)}
                        >
                          生成柱状图
                        </Button>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => exportToCSV(channelToken.filteredData, channelTokenExportCols, `渠道模型Token统计_${dayjs().format('YYYYMMDDHHmmss')}.csv`)}
                        >
                          导出CSV
                        </Button>
                      </Space>
                    </div>
                    <div className="filter-bar">
                      <span className="filter-bar__label">时间范围</span>
                      <RangePicker
                        showTime
                        value={channelToken.dateRange}
                        onChange={(dates) => channelToken.setDateRange(dates)}
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                        style={{ width: 420 }}
                      />
                      <span className="filter-bar__label">模型</span>
                      <Select
                        mode="multiple"
                        placeholder="全部模型"
                        allowClear
                        maxTagCount={2}
                        style={{ minWidth: 200 }}
                        value={channelToken.selectedModels}
                        onChange={(values) => channelToken.setSelectedModels(values || [])}
                        options={channelToken.modelOptions.map(m => ({ label: m, value: m }))}
                      />
                      <div style={{ flex: 1 }} />
                      <Button type="primary" icon={<SearchOutlined />} onClick={channelToken.handleSearch}>查询</Button>
                      <Button icon={<ReloadOutlined />} onClick={channelToken.handleRefresh}>刷新</Button>
                    </div>
                    <div className="table-fixed-height">
                      <Table
                        bordered={false}
                        showSorterTooltip={false}
                        columns={channelTokenColumns}
                        dataSource={channelToken.filteredData}
                        loading={channelToken.loading}
                        rowKey="model_name"
                        scroll={{ y: 300 }}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  </div>

                </div>
              </div>
            </section>

            <Modal
              title="生成用户维度Token柱状图"
              open={userChartModalVisible}
              onCancel={() => { setUserChartModalVisible(false); userChartForm.resetFields(); }}
              onOk={() => handleExportBarChart({ type: 'user' })}
              okText="确定生成"
              cancelText="取消"
              destroyOnClose
            >
              <Form form={userChartForm} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item name="fieldKey" label="统计字段" initialValue="total_tokens" rules={[{ required: true, message: '请选择统计字段' }]}>
                  <Select options={userTokenChartFields} />
                </Form.Item>
                <Form.Item name="sortOrder" label="排序方式" initialValue="desc" rules={[{ required: true, message: '请选择排序方式' }]}>
                  <Select
                    options={[
                      { label: '降序', value: 'desc' },
                      { label: '升序', value: 'asc' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="unit" label="单位" initialValue="wan" rules={[{ required: true, message: '请选择单位' }]}>
                  <Select
                    options={[
                      { label: '万', value: 'wan' },
                      { label: '个', value: 'count' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="limit" label="导出数量" initialValue={10} rules={[{ required: true, message: '请输入导出数量' }]}>
                  <InputNumber min={1} max={500} precision={0} style={{ width: '100%' }} placeholder="请输入导出多少个" />
                </Form.Item>
              </Form>
            </Modal>

            <Modal
              title="生成渠道模型Token柱状图"
              open={channelChartModalVisible}
              onCancel={() => { setChannelChartModalVisible(false); channelChartForm.resetFields(); }}
              onOk={() => handleExportBarChart({ type: 'channel' })}
              okText="确定生成"
              cancelText="取消"
              destroyOnClose
            >
              <Form form={channelChartForm} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item name="fieldKey" label="统计字段" initialValue="total_tokens" rules={[{ required: true, message: '请选择统计字段' }]}>
                  <Select options={tokenChartFields} />
                </Form.Item>
                <Form.Item name="sortOrder" label="排序方式" initialValue="desc" rules={[{ required: true, message: '请选择排序方式' }]}>
                  <Select
                    options={[
                      { label: '降序', value: 'desc' },
                      { label: '升序', value: 'asc' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="unit" label="单位" initialValue="wan" rules={[{ required: true, message: '请选择单位' }]}>
                  <Select
                    options={[
                      { label: '万', value: 'wan' },
                      { label: '个', value: 'count' },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="limit" label="导出数量" initialValue={10} rules={[{ required: true, message: '请输入导出数量' }]}>
                  <InputNumber min={1} max={500} precision={0} style={{ width: '100%' }} placeholder="请输入导出多少个" />
                </Form.Item>
              </Form>
            </Modal>

            {/* ==================== 模块2: 用户管理 ==================== */}
            <section id="user-management" className="admin-section">
              <div className="module-card">
                <div className="module-card__header">
                  <div className="section-title">
                    <div className="section-title__icon section-title__icon--green">
                      <UserOutlined />
                    </div>
                    <span className="section-title__text">用户管理</span>
                    <div className="section-title__line" />
                  </div>
                </div>
                <div className="module-card__content">
                  <div className="user-summary" aria-label="用户统计">
                    {[
                      ['用户总数', userMgmt.summary.total],
                      ['管理员', userMgmt.summary.admins],
                      ['普通用户', userMgmt.summary.regular],
                      ['当前在线', userMgmt.summary.online ?? '—'],
                    ].map(([label, value]) => (
                      <div className="user-summary__item" key={label}>
                        <span className="user-summary__label">{label}</span>
                        <strong className="user-summary__value">{value}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="filter-bar">
                    <span className="filter-bar__label">用户名</span>
                    <Select
                      mode="multiple"
                      placeholder="全部用户"
                      allowClear
                      maxTagCount={2}
                      style={{ minWidth: 200 }}
                      value={userMgmt.selectedUsernames}
                      onChange={(values) => userMgmt.setSelectedUsernames(values || [])}
                      options={userMgmt.usernameOptions.map(u => ({ label: u, value: u }))}
                    />
                    <div style={{ flex: 1 }} />
                    <Button icon={<ReloadOutlined />} onClick={userMgmt.loadUsers}>刷新</Button>
                    <Popconfirm
                      title="确认重置所有非管理员密码？"
                      description="将为所有非管理员用户生成随机密码并同步到关联平台；管理员账号不受影响。用户可使用富工作扫码登录。"
                      okText="确认重置"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      onConfirm={userMgmt.handleResetNonAdminPasswords}
                    >
                      <Button danger icon={<SafetyCertificateOutlined />} loading={userMgmt.resetPwdRunning}>
                        重置非管理员密码
                      </Button>
                    </Popconfirm>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => userMgmt.setAddUserModalVisible(true)}>添加用户</Button>
                  </div>
                  <div className="table-fixed-height--lg">
                    <Table
                      bordered={false}
                      showSorterTooltip={false}
                      columns={userColumns}
                      dataSource={userMgmt.filteredUsers}
                      loading={userMgmt.loading}
                      rowKey="user_id"
                      scroll={{ x: 1480, y: 400 }}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </div>
              </div>

              {/* 添加用户Modal */}
              <Modal
                title="添加用户"
                open={userMgmt.addUserModalVisible}
                onCancel={() => { userMgmt.setAddUserModalVisible(false); addUserForm.resetFields(); }}
                onOk={handleAddUserSubmit}
                confirmLoading={userMgmt.addUserSubmitting}
                okText="确认添加"
                cancelText="取消"
                destroyOnClose
              >
                <Form form={addUserForm} layout="vertical" style={{ marginTop: 16 }}>
                  <Form.Item name="username" label="用户名" rules={[{ required: true, message: '请输入用户名' }]}>
                    <Input placeholder="请输入用户名" />
                  </Form.Item>
                  <Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}>
                    <Input placeholder="请输入邮箱地址" />
                  </Form.Item>
                  <Form.Item
                    name="username_alias"
                    label="短用户名"
                    extra="OIDC 的 preferred_username 将使用此值，全局唯一。"
                    rules={[
                      { required: true, message: '请输入短用户名' },
                      {
                        pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/,
                        message: '只能包含字母、数字、点、下划线和中划线，且以字母或数字开头',
                      },
                    ]}
                  >
                    <Input placeholder="例如 fenglin" maxLength={64} />
                  </Form.Item>
                  <Form.Item name="pwd" label="密码" rules={[{ required: true, message: '请输入密码' }]}>
                    <Input.Password placeholder="请输入密码" />
                  </Form.Item>
                  <Form.Item name="force_update_password" label="是否强制修改密码" initialValue="0" rules={[{ required: true, message: '请选择是否强制修改密码' }]}>
                    <Select
                      placeholder="请选择是否强制修改密码"
                      options={[
                        { label: '强制修改', value: '0' },
                        { label: '不强制', value: '1' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item name="is_admin" label="是否管理员" valuePropName="checked" initialValue={false}>
                    <Switch checkedChildren="管理员" unCheckedChildren="普通用户" />
                  </Form.Item>
                </Form>
              </Modal>

              {/* 修改密码Modal */}
              <Modal
                title="修改密码"
                open={userMgmt.editPwdModalVisible}
                onCancel={() => { userMgmt.setEditPwdModalVisible(false); editPwdForm.resetFields(); }}
                onOk={handleEditPwdSubmit}
                confirmLoading={userMgmt.editPwdSubmitting}
                okText="确认修改"
                cancelText="取消"
                destroyOnClose
              >
                <Form form={editPwdForm} layout="vertical" style={{ marginTop: 16 }}>
                  <Form.Item label="用户名">
                    <div className="admin-readonly-value">{userMgmt.editingUser?.username || '-'}</div>
                  </Form.Item>
                  <Form.Item label="当前密码（只读）">
                    <div className="admin-readonly-value admin-readonly-value--mono">
                      {userMgmt.editingUser?.pwd || '-'}
                    </div>
                  </Form.Item>
                  <Form.Item name="new_pwd" label="新密码" rules={[{ required: true, message: '请输入新密码' }]}>
                    <Input.Password ref={editPwdInputRef} placeholder="请输入新密码" />
                  </Form.Item>
                </Form>
              </Modal>

              {/* 短用户名Modal */}
              <Modal
                title="设置短用户名"
                open={userMgmt.aliasModalVisible}
                onCancel={() => { userMgmt.setAliasModalVisible(false); aliasForm.resetFields(); }}
                onOk={handleAliasSubmit}
                confirmLoading={userMgmt.aliasSubmitting}
                okText="保存"
                cancelText="取消"
                destroyOnClose
              >
                <Form form={aliasForm} layout="vertical" style={{ marginTop: 16 }}>
                  <Form.Item label="用户">
                    <div className="admin-readonly-value">{userMgmt.editingAliasUser?.username || '-'}</div>
                  </Form.Item>
                  <Form.Item
                    name="username_alias"
                    label="短用户名"
                    extra="OIDC 的 preferred_username 使用此值，全局唯一。"
                    rules={[
                      { required: true, message: '请输入短用户名' },
                      {
                        pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/,
                        message: '只能包含字母、数字、点、下划线和中划线，且以字母或数字开头',
                      },
                    ]}
                  >
                    <Input placeholder="例如 fenglin" maxLength={64} />
                  </Form.Item>
                </Form>
              </Modal>
            </section>

            {/* ==================== 模块3: AI工具管理 ==================== */}
            <section id="agent-management" className="admin-section">
              <div className="module-card">
                <div className="module-card__header">
                  <div className="section-title">
                    <div className="section-title__icon section-title__icon--purple">
                      <RobotOutlined />
                    </div>
                    <span className="section-title__text">AI工具管理</span>
                    <div className="section-title__line" />
                  </div>
                </div>
                <div className="module-card__content">
                  <div className="filter-bar">
                    <span className="filter-bar__label">工具名称</span>
                    <Select
                      mode="multiple"
                      placeholder="全部工具"
                      allowClear
                      maxTagCount={2}
                      style={{ minWidth: 200 }}
                      value={agentMgmt.selectedNames}
                      onChange={(values) => agentMgmt.setSelectedNames(values || [])}
                      options={agentMgmt.nameOptions.map(n => ({ label: n, value: n }))}
                    />
                    <span className="filter-bar__label">状态</span>
                    <Select
                      mode="multiple"
                      placeholder="全部状态"
                      allowClear
                      maxTagCount={2}
                      style={{ minWidth: 180 }}
                      value={agentMgmt.selectedStatus}
                      onChange={(values) => agentMgmt.setSelectedStatus(values || [])}
                      options={statusOptions}
                    />
                    <div style={{ flex: 1 }} />
                    <Button icon={<ReloadOutlined />} onClick={agentMgmt.loadAgents}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenAgentModal('add')}>添加工具</Button>
                  </div>
                  <div className="table-fixed-height--lg">
                    <Table
                      bordered={false}
                      showSorterTooltip={false}
                      columns={agentColumns}
                      dataSource={agentMgmt.filteredAgents}
                      loading={agentMgmt.loading}
                      rowKey="agent_id"
                      scroll={{ y: 400 }}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </div>
              </div>

              {/* 添加/编辑工具Modal */}
              <Modal
                title={agentMgmt.modalMode === 'add' ? '添加工具' : '编辑工具'}
                open={agentMgmt.modalVisible}
                onCancel={() => { agentMgmt.setModalVisible(false); agentForm.resetFields(); }}
                onOk={handleSubmitAgent}
                confirmLoading={agentMgmt.submitting}
                okText={agentMgmt.modalMode === 'add' ? '确认添加' : '确认更新'}
                cancelText="取消"
                destroyOnClose
                width={560}
              >
                <Form form={agentForm} layout="vertical" style={{ marginTop: 16 }}>
                  {agentMgmt.modalMode === 'edit' && (
                    <Form.Item name="agent_id" label="工具ID">
                      <Input disabled />
                    </Form.Item>
                  )}
                  <Form.Item name="agent_name" label="工具名称" rules={[{ required: true, message: '请输入工具名称' }]}>
                    <Input placeholder="请输入工具名称" />
                  </Form.Item>
                  <Form.Item name="agent_tags" label="标签">
                    <Input placeholder="多个标签用逗号分隔，如：办公,写作" />
                  </Form.Item>
                  <Form.Item name="agent_type" label="工具类型" initialValue="chat">
                    <Select options={agentTypeOptions} />
                  </Form.Item>
                  <Form.Item name="agent_icon" label="卡片图标" initialValue="">
                    <Select options={getAgentIconSelectOptions()} placeholder="默认按名称/类型自动选择" />
                  </Form.Item>
                  <Form.Item name="agent_des" label="描述">
                    <Input.TextArea rows={3} placeholder="请输入工具描述" />
                  </Form.Item>
                  <Form.Item name="agent_jump_url" label="跳转链接">
                    <Input placeholder="请输入跳转URL" />
                  </Form.Item>
                  <Form.Item
                    name="is_support_email"
                    label="支持邮箱登录"
                    valuePropName="checked"
                    initialValue={true}
                  >
                    <Switch checkedChildren="支持" unCheckedChildren="不支持" />
                  </Form.Item>
                  <Form.Item name="status" label="状态" initialValue="1">
                    <Select options={statusOptions} />
                  </Form.Item>
                  {agentMgmt.modalMode === 'add' && (
                    <>
                      <Form.Item name="dep_ids" label="授权部门ID">
                        <Input placeholder="多个ID用逗号分隔，如：dep_001,dep_002" />
                      </Form.Item>
                      <Form.Item name="role_ids" label="授权角色ID">
                        <Input placeholder="多个ID用逗号分隔，如：role_001,role_002" />
                      </Form.Item>
                    </>
                  )}
                </Form>
              </Modal>
            </section>

            {/* ==================== 模块4: 公告管理 ==================== */}
            <section id="announcement-management" className="admin-section">
              <div className="module-card">
                <div className="module-card__header">
                  <div className="section-title">
                    <div className="section-title__icon section-title__icon--orange">
                      <NotificationOutlined />
                    </div>
                    <span className="section-title__text">公告管理</span>
                    <div className="section-title__line" />
                  </div>
                </div>
                <div className="module-card__content">
                  <div className="action-bar">
                    <Button icon={<ReloadOutlined />} onClick={announcementMgmt.loadAnnouncements}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => announcementMgmt.setAddModalVisible(true)}>发布公告</Button>
                  </div>
                  <div className="table-fixed-height--lg">
                    <Table
                      bordered={false}
                      showSorterTooltip={false}
                      columns={announcementColumns}
                      dataSource={announcementMgmt.announcements}
                      loading={announcementMgmt.loading}
                      rowKey="id"
                      scroll={{ y: 400 }}
                      pagination={false}
                      size="small"
                    />
                  </div>
                </div>
              </div>

              {/* 发布公告Modal */}
              <Modal
                title="发布公告"
                open={announcementMgmt.addModalVisible}
                onCancel={() => { announcementMgmt.setAddModalVisible(false); addAnnouncementForm.resetFields(); }}
                onOk={handleAddAnnouncementSubmit}
                confirmLoading={announcementMgmt.addSubmitting}
                okText="确认发布"
                cancelText="取消"
                destroyOnClose
              >
                <Form form={addAnnouncementForm} layout="vertical" style={{ marginTop: 16 }}>
                  <Form.Item name="public_content" label="公告内容" rules={[{ required: true, message: '请输入公告内容' }]}>
                    <Input.TextArea rows={6} placeholder="请输入公告内容" />
                  </Form.Item>
                </Form>
              </Modal>

              {/* 编辑公告Modal */}
              <Modal
                title="编辑公告"
                open={announcementMgmt.editModalVisible}
                onCancel={() => { announcementMgmt.setEditModalVisible(false); editAnnouncementForm.resetFields(); }}
                onOk={handleEditAnnouncementSubmit}
                confirmLoading={announcementMgmt.editSubmitting}
                okText="保存"
                cancelText="取消"
                destroyOnClose
              >
                <Form form={editAnnouncementForm} layout="vertical" style={{ marginTop: 16 }}>
                  <Form.Item name="id" label="公告ID">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item name="public_content" label="公告内容" rules={[{ required: true, message: '请输入公告内容' }]}>
                    <Input.TextArea rows={6} placeholder="请输入公告内容" />
                  </Form.Item>
                  <Form.Item name="created_time" label="发布时间" rules={[{ required: true, message: '请选择发布时间' }]}>
                    <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} placeholder="选择发布时间" />
                  </Form.Item>
                </Form>
              </Modal>
            </section>

          </main>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default AdminDashboard;
